import { useCallback, useContext, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Modal from 'LuxKit/Modal/Modal'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { syncUserDetails } from 'actions/AuthActions'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import { reportClientError } from 'services/errorReportingService'
import {
  getLocaleDirection,
  getLocaleLanguageCode,
  type VisibleLanguageOption,
} from 'i18n/util/locale'
import SpoofTargetLanguageProvider from './SpoofTargetLanguageProvider'

const TargetLanguageModal = styled(Modal).attrs<{ $locale: string }>(props => ({
  dir: getLocaleDirection(props.$locale),
  lang: getLocaleLanguageCode(props.$locale),
}))``

function CustomerLanguageConfirmation({
  language,
  onChangeOnlyForSpoof,
}: {
  language: VisibleLanguageOption
  onChangeOnlyForSpoof: (language: VisibleLanguageOption) => void
}) {
  return <SpoofTargetLanguageProvider locale={language.locale}>
    <CustomerLanguageConfirmationContent
      language={language}
      onChangeOnlyForSpoof={onChangeOnlyForSpoof}
    />
  </SpoofTargetLanguageProvider>
}

function CustomerLanguageConfirmationContent({
  language,
  onChangeOnlyForSpoof,
}: {
  language: VisibleLanguageOption
  onChangeOnlyForSpoof: (language: VisibleLanguageOption) => void
}) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { resolve } = useModalElementContext()
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const saving = useRef(false)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  const cancel = useCallback(() => {
    if (!saving.current) {
      onChangeOnlyForSpoof(language)
      resolve()
    }
  }, [language, onChangeOnlyForSpoof, resolve])

  const close = useCallback(() => {
    if (!saving.current) resolve()
  }, [resolve])

  const confirm = useCallback(async() => {
    if (!isSpoofed || saving.current) return
    saving.current = true
    setLoading(true)
    setFailed(false)
    try {
      await updateCustomerLanguagePreferenceFromAuth(language.locale)
      dispatch(syncUserDetails({ language: language.locale }))
      onChangeOnlyForSpoof(language)
      resolve()
    } catch(error) {
      reportClientError(error)
      setFailed(true)
    } finally {
      saving.current = false
      setLoading(false)
    }
  }, [dispatch, isSpoofed, language, onChangeOnlyForSpoof, resolve])

  return <TargetLanguageModal
    $locale={language.locale}
    title={intl.formatMessage({
      id: 'SNCWRtptlW7th5xf4kcV',
      defaultMessage: 'Change customer’s language?',
    })}
    primaryActionText={intl.formatMessage({
      id: 'SR7je7gD5OBVN3747SwG',
      defaultMessage: 'Yes, change for customer',
    })}
    secondaryActionText={intl.formatMessage({
      id: 'XD9poMuAHYt7tRDwuegl',
      defaultMessage: 'No, only change for me',
    })}
    onPrimaryActionClick={confirm}
    onSecondaryActionClick={cancel}
    onClose={close}
    dismissible={!loading}
    isPrimaryActionDisabled={loading || !isSpoofed}
    isSecondaryActionDisabled={loading}
  >
    <BodyText variant="large" colour="critical">
      {intl.formatMessage({
        id: '0Pph1dj6XZzsV_Yqpf0L',
        defaultMessage: 'Warning: confirming changes the language for both you and the customer to {language}.',
      }, { language: language.nativeName })}
    </BodyText>
    {failed && <BodyText variant="medium" colour="critical" role="alert">
      {intl.formatMessage({
        id: 'RjmCeUjm-RLEN0O4xb-u',
        defaultMessage: 'The customer’s language could not be saved. Please try again.',
      })}
    </BodyText>}
  </TargetLanguageModal>
}

export default function SpoofCustomerLanguageButton({
  language,
  onChangeOnlyForSpoof,
}: {
  language: VisibleLanguageOption
  onChangeOnlyForSpoof: (language: VisibleLanguageOption) => void
}) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const [pending, setPending] = useState(false)
  const open = useCallback(async() => {
    setPending(true)
    try {
      await showModal(
        <CustomerLanguageConfirmation language={language} onChangeOnlyForSpoof={onChangeOnlyForSpoof} />,
      )
    } finally {
      setPending(false)
    }
  }, [language, onChangeOnlyForSpoof, showModal])

  return <TextButton kind="tertiary" size="small" onClick={open} disabled={pending}>
    <BodyText variant="small" colour="critical">
      {intl.formatMessage({
        id: 'NHIG7ASJv18cauuPFR0z',
        defaultMessage: 'Change for customer',
      })}
    </BodyText>
  </TextButton>
}
